import styled, { createGlobalStyle, DefaultTheme } from "styled-components";
import { parseToHsl, hsl } from "polished";
import Global from "./Global";

import {
  Colors,
  Grayscale,
  FontSizes,
  Sizes,
  Transitions,
  ThemeParams
} from "../types";

export const makeColorScale = (color: string, steps: number) => {
  const base = parseToHsl(color);
  let colors = [];
  for (var i = 0; i < steps; ++i) {
    colors.push(
      hsl({
        hue: base.hue,
        saturation: base.saturation,
        lightness: Math.pow(i / (steps - 1), 0.8)
      })
    );
  }
  return colors;
};

const applyGrayscale = (colors: string[]): Grayscale => {
  return {
    g0: colors[10],
    g10: colors[9],
    g20: colors[8],
    g30: colors[7],
    g40: colors[6],
    g50: colors[5],
    g60: colors[4],
    g70: colors[3],
    g80: colors[2],
    g90: colors[1],
    g100: colors[0]
  };
};

export const makeTheme = (params: ThemeParams): DefaultTheme => {
  const isDark = params.isDark || false;
  const scale = params.textScale || 1.33;
  const mobileScale = params.mobileTextScale || 1.15;
  const fontSizes: FontSizes = {
    base: params.baseFontSize || 20,
    h1: Math.pow(scale, 5),
    h2: Math.pow(scale, 4),
    h3: Math.pow(scale, 3),
    h4: Math.pow(scale, 2),
    h5: Math.pow(scale, 1),
    h6: 1,
    p: 1,
    small: 0.8
  };
  const mobileFontSizes: FontSizes = {
    base: params.baseFontSize || 20,
    h1: Math.pow(mobileScale, 5),
    h2: Math.pow(mobileScale, 4),
    h3: Math.pow(mobileScale, 3),
    h4: Math.pow(mobileScale, 2),
    h5: Math.pow(mobileScale, 1),
    h6: 1,
    p: 1,
    small: 0.8
  };
  let colors: Colors = {
    primary: params.primaryColor || "#0e75e0",
    danger: params.dangerColor || "#dc4e41",
    background: isDark ? "black" : "white",
    inverse: isDark ? "white" : "black"
  };

  let sizes: Sizes = {
    tiny: 2 / 16,
    small: 4 / 16,
    normal: 8 / 16,
    large: 16 / 16,
    huge: 32 / 16,
    mega: 64 / 16
  };

  let transitions: Transitions = {
    quick: "220ms",
    medium: "500ms",
    slow: "1000ms"
  };

  return {
    isDark,
    fontSizes,
    mobileFontSizes,
    colors,
    sizes,
    transitions,
    grayscale: isDark
      ? applyGrayscale(makeColorScale("black", 11).reverse())
      : applyGrayscale(makeColorScale("black", 11)),
    breakpoints: {
      mobile: 576,
      tablet: 768,
      desktop: 992,
      large: 1200
    }
  };
};

export const LightTheme: DefaultTheme = makeTheme({
  isDark: false
});

export const DarkTheme: DefaultTheme = makeTheme({
  isDark: true
});

export const LightGlobalStyle = createGlobalStyle`
${Global};
`;

export const DarkGlobalStyle = createGlobalStyle`
${Global};

html, body {
  background-color: black;
  color: white;
}
`;

export const Background = styled.div`
  background-color: ${props => props.theme.colors.background};
`;
